import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Container,
  Divider,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  GlobalStyles,
  Chip,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Person as PersonIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentCopy as ContentCopyIcon,
  AccountCircle as AccountCircleIcon,
  Info as InfoIcon,
  ErrorOutline as ErrorOutlineIcon,
  ThumbUp,
  ChatBubbleOutline,
  OpenInNew as OpenInNewIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import engagementService from '../services/engagementService';
import authService from '../services/authService';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Masonry from 'react-masonry-css';
import PostCard from '../components/PostCard';
import { useSnackbar } from 'notistack';
import { Skeleton } from '@mui/material';
import FeatureLockOverlay from '../components/FeatureLockOverlay';
import api from '../utils/api';
import { Helmet } from 'react-helmet-async';
import EngagementPostCard from '../components/EngagementPostCard';
import LoadingScreen from '../components/common/LoadingScreen';
import { grey } from '@mui/material/colors';

// Import SVG as ReactComponent  
import { ReactComponent as PostReactions } from './post-reactions.svg';

// Add the comment prompts
const commentPrompts = {
  "Simple Tone": "The above is a LinkedIn post, respond to the post professionally.",
  "New Insight": "The above is a LinkedIn post, and I would like to comment on it to engage with the author. The author is a senior executive at a large enterprise. I have never met and spoken with the author before, so our engagement must be professional and casual. Please don't be rude.\n\nThe objective is to share new insight so I can be seen as authoritative in the topic discussed in the post. The suggested comment will include brand-new insights in response to the post above. It should also include one example used in the industry. Use the first-person point of view in the comment. Do not paraphrase. Use new words, phrases, ideas, and insights with a new angle.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited. You can use hyphens but make sure not to use en dashes, and em dashes. Keep the output under 20 words. Please don't use emojis or hashtags. Avoid any imbalanced quotation in the comment.",
  "Sell": "The above is a LinkedIn post, and I would like to comment on it to engage with the author. The author is a senior executive at a large enterprise. I have messaged and emailed the author, so the engagement can be reasonably casual but must remain professional.\n\nThe objective is to share my expertise so they know I can deliver results that meet their expectations.\n\nThe suggested comment will include an example used in the industry as discussed in the post. Please also provide social proof by casually mentioning that I have repeatedly solved this for our clients. Finish the comment by saying I will send them a DM about a case study they may find helpful. Use the first-person point of view in the comment.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nDo not use hyphens en-dash, and em-dash. Please keep the comment under 75 words, and don't use emojis or hashtags. Avoid any imbalance quotations in the comment.",
  "Industry Leader": "The above is a LinkedIn post, and I would like to comment on it to engage with the author and position myself as an expert for the topic discussed. The author is a senior executive at a large enterprise.\n\nThe objective is to share my business acumen, technical know-how and expertise. The suggested comment will mention a book or research I have authored on the topic discussed in the post. It should also include an example of its application in the industry. Use the first-person point of view in the comment.\n\nAsk for their permission for me to send them a DM, if they would be interested in a free copy as a gift.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nPlease keep the output under 45 words, and don't use emojis or hashtags. Avoid any imbalance quotation in the generated comment.",
  "Lighthearted": "The above is a LinkedIn post, and I would like to comment on it to build rapport with the author.\n\nBe a cheerful and light-hearted LinkedIn user. Reply to this LinkedIn post with a comment that contains a touch of humor or amusement while still being respectful and relevant. Go beyond jokes involving coffee and try a variety of broader topics. I want the joke to apply to the context of the content above.\n\nPlease keep the comment under 75 words, and include appropriate emojis or hashtags. Avoid any imbalance quotations in the generated comment.",
  "Reply Comment": "The above is a comment replied to on a LinkedIn post, and I would like to respond to it and engage with the commenter. The author is a senior executive at a large enterprise.\n\nThe objective is to start a conversation where we can continue by moving it to a DM.\n\nHere is how the comment should be structured:\n\nStart by thanking the person for sharing their thoughts.\n\nDo not paraphrase, but use new words, phrases, ideas, and insights from a new angle to add one insight in a single sentence. Subsequently, I would like to ask them to elaborate on their suggestions so I can understand their perspective. Use the first-person point of view in the comment.\n\nMake sure to avoid repeating what has already been said in the LinkedIn post. You can use hyphens but make sure not to use en dashes, and em dashes. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nDo not include hashtags or emojis. Avoid any imbalance quotations in the generated comment. Please keep it to less than 50 words.",
  "Repost": "The above is a LinkedIn post, and I would like to build rapport with the author by resharing their content because I found the information to be valuable and inspiring.\n\nStart the message by giving credit to the author. Highlight one of the most crucial ideas suggested in the original content. Highlight why we can trust the author by quoting their background. Explain why I found it inspiring by quoting their insights and background information. Use the first-person point of view in the comment.\n\nI will share the post with my targeted audience, which is senior executives at large enterprises. Remember, this is a professional post, so refrain from using emojis to maintain a serious tone. You can use hyphens but make sure not to use en dashes, and em dashes. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nThe output is around 150 to 200 words long. No emojis but we can include appropriate hashtags relevant to the content to increase content discovery by the LinkedIn algorithm. Avoid any imbalanced double quotation in the generated comment.",
  "Answer a Question": "The above is a LinkedIn post, and I would like to build rapport and start a conversation with the author by answering the question in their post.\n\nStart by identifying the question left within the post. Once you identify the question, craft me a comment to answer the question so I can join the conversation. Elaborate the answer with some of my personal experiences to make it more relatable. Use the first-person point of view in the comment.\n\nDo not repeat the question you found but provide an answer for the question.\n\nDo not paraphrase but use new words, phrases, ideas, and insights with a new angle. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nYou can use hyphens but make sure not to use en dashes, and em dashes. Keep the output in less than 45 words.\n\nNo emoji and no hashtags. Avoid any imbalance quotations in the generated comment.",
  "Ask a Question": "The above is a LinkedIn post, and I would like to build rapport and start a conversation with the author by asking a question that's relevant to their post.\n\nStart by understanding the context of the content and the author's author's background.\n\nOnce you have the full context, craft me a question as a comment to the author. Keep the question suggestions short and simple. Use the first-person point of view in the comment.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nYou can use hyphens but make sure not to use en dashes, and em dashes. Keep the output in less than 45 words.\n\nNo emoji and no hashtags. Avoid any imbalance quotations in the generated comment.",
  "Support Author": "The above is a LinkedIn post, and I would like to build rapport with the author by supporting them on LinkedIn.\n\nWrite me a straightforward sentence describing I'm commenting for broader reach so people could see their post. Avoid any imbalance quotation in the generated comment.",
  "Share Resume": "The above is a LinkedIn post, and I would like to comment and position myself as an expert for the topic discussed so that I can get a job at their company. The author is a hiring manager for the company for which I want to work.\n\nThe objective is to share new insight so I can be seen as authoritative in the topic discussed in the post. The suggested comment will include brand-new insights in response to the post above. Ideally, it should also include one example used in the industry. Finally, let them know that I'm looking for a new opportunity and it is ok if I send them my resume by a DM. Use the first-person point of view in the comment.\n\nDo not paraphrase. Use new words, phrases, ideas, and insights with a new angle.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nPlease keep the output under 60 words, and don't use emojis or hashtags. You can use hyphens but make sure not to use en dashes, and em dashes. Avoid any imbalance quotation in the generated comment."
};

const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    sx={{ p: 3 }}
  >
    {value === index && children}
  </Box>
);

const masonryStyles = {
  '.masonry-grid': {
    display: 'flex',
    marginLeft: '-16px',
    width: 'auto'
  },
  '.masonry-grid_column': {
    paddingLeft: '16px',
    backgroundClip: 'padding-box'
  }
};

const extractUsername = (url) => {
  if (!url) return null;
  const matches = url.match(/linkedin\.com\/in\/([^\/]+)/);
  return matches ? matches[1] : null;
};

const extractLinkedInName = (url) => {
  try {
    const username = extractUsername(url);
    if (username) {
      return username
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
  } catch (e) {
    console.error('Error parsing LinkedIn URL for name:', e);
  }
  return 'LinkedIn User';
};

const formatNumber = (num) => {
  if (!num) return 0;
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num;
};

const EngagementProfiles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [engagementList, setEngagementList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showListDetails, setShowListDetails] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [newProfileUrl, setNewProfileUrl] = useState('');
  const [addingProfile, setAddingProfile] = useState(false);
  const [openAddProfileDialog, setOpenAddProfileDialog] = useState(false);
  const [apiError, setApiError] = useState('');
  const [currentTab, setCurrentTab] = useState('profiles');
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(true);
  const [refreshTime, setRefreshTime] = useState('6');
  const [refreshPeriod, setRefreshPeriod] = useState('AM');
  const [optOutEmail, setOptOutEmail] = useState(false);
  const [profilePosts, setProfilePosts] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentingPost, setCommentingPost] = useState(null);
  const [openRewritePanel, setOpenRewritePanel] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState('');
  const [rewriteTopic, setRewriteTopic] = useState('');
  const [selectedCommentPrompt, setSelectedCommentPrompt] = useState("Simple Tone");
  const [generatingComment, setGeneratingComment] = useState(false);
  const [selectedPostForRewrite, setSelectedPostForRewrite] = useState(null);
  const [postSources, setPostSources] = useState({});
  
  /* Commented out settings state variables
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(true);
  const [refreshTime, setRefreshTime] = useState('6');
  const [refreshPeriod, setRefreshPeriod] = useState('AM');
  const [optOutEmail, setOptOutEmail] = useState(false);
  */

  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(true);
  const [isFeatureAllowed, setIsFeatureAllowed] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const PRO_PLAN_ID = process.env.REACT_APP_PADDLE_PRO_MONTHLY;
  const PRO_YEARLY_ID = process.env.REACT_APP_PADDLE_PRO_YEARLY;
  const BUSINESS_PLAN_ID = process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY;
  const BUSINESS_YEARLY_ID = process.env.REACT_APP_PADDLE_BUSINESS_YEARLY;

  const showAlert = useCallback((message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  }, []);

  const handleAlertClose = () => {
    setOpenAlert(false);
  };

  const fetchProfilePosts = useCallback(async (currentList = engagementList, forceApi = false) => {
    if (!currentList || !currentList.id || !currentList.profiles || currentList.profiles.length === 0) {
      showAlert('No profiles in your custom feed to fetch posts from.', 'warning');
      setProfilePosts([]);
      return;
    }
    
    setLoading(true);
    setProfilePosts([]);
    setPostSources({});
    let allPosts = [];
    let newPostSources = {};

    try {
      const profileUsernames = currentList.profiles.map(profile => extractUsername(profile.profileUrl)).filter(username => !!username);

      if (profileUsernames.length === 0) {
        showAlert('No valid LinkedIn profile usernames to fetch posts for.', 'warning');
        setLoading(false);
        return;
      }

      if (forceApi) {
        // Fetch directly from API
        console.log(`Fetching posts from API for LinkedIn profiles: ${profileUsernames.join(', ')}`);
        const apiResponse = await api.post('/api/linkedin/engagement-profile-posts', {
          engageAccounts: profileUsernames,
          forceApi: true
        });

        if (apiResponse.data.success && apiResponse.data.posts?.tweets?.length > 0) {
          allPosts = apiResponse.data.posts.tweets;
          console.log('Got posts from API:', allPosts.length);
          
          // Track API post sources
          allPosts.forEach(post => {
            const username = post.author?.username || extractUsername(post.author?.profileUrl);
            if (username) {
              newPostSources[username] = 'api';
            }
          });
        }
      } else {
        // Fetch from database first
        console.log(`Fetching posts from database for LinkedIn profiles: ${profileUsernames.join(', ')}`);
        const dbResponse = await api.post('/api/linkedin/engagement-profile-posts', {
          engageAccounts: profileUsernames,
          forceDatabase: true
        });

        if (dbResponse.data.success && dbResponse.data.posts?.tweets?.length > 0) {
          const dbPosts = dbResponse.data.posts.tweets;
          console.log('Got posts from database:', dbPosts.length);
          allPosts = dbPosts;
          
          // Get posts from database by profile
          const dbPostsByProfile = dbPosts.reduce((acc, post) => {
            const username = post.author?.username || extractUsername(post.author?.profileUrl);
            if (username) {
              if (!acc[username]) acc[username] = [];
              acc[username].push(post);
            }
            return acc;
          }, {});

          console.log('Database posts by profile (detailed):', dbPostsByProfile);

          // Find profiles with no posts in database
          const profilesNeedingApiFetch = profileUsernames.filter(username => 
            !dbPostsByProfile[username] || dbPostsByProfile[username].length === 0
          );

          if (profilesNeedingApiFetch.length > 0) {
            console.log('Fetching from API for profiles with no database posts:', profilesNeedingApiFetch);
            const apiResponse = await api.post('/api/linkedin/engagement-profile-posts', {
              engageAccounts: profilesNeedingApiFetch,
              forceApi: true
            });

            if (apiResponse.data.success && apiResponse.data.posts?.tweets?.length > 0) {
              const apiPosts = apiResponse.data.posts.tweets;
              console.log('Got posts from API:', apiPosts.length);
              
              // Log API posts by profile
              const apiPostsByProfile = apiPosts.reduce((acc, post) => {
                const username = post.author?.username || extractUsername(post.author?.profileUrl);
                if (username) {
                  if (!acc[username]) acc[username] = [];
                  acc[username].push(post);
                }
                return acc;
              }, {});
              
              console.log('API posts by profile (detailed):', apiPostsByProfile);
              
              // Add API posts to allPosts
              allPosts = [...allPosts, ...apiPosts];
              
              // Track API post sources
              apiPosts.forEach(post => {
                const username = post.author?.username || extractUsername(post.author?.profileUrl);
                if (username) {
                  newPostSources[username] = 'api';
                }
              });
            }
          }
        }
      }

      // Remove any duplicates based on post ID
      const uniquePosts = allPosts.filter((post, index, self) =>
        index === self.findIndex((p) => p.id === post.id)
      );

      console.log('Setting unique posts:', uniquePosts.length);
      setProfilePosts(uniquePosts);
      setPostSources(newPostSources);

      if (uniquePosts.length === 0) {
        showAlert('No posts found for any of the profiles.', 'info');
      }

    } catch (err) {
      console.error('Error in fetchProfilePosts:', err);
      showAlert(err.message || 'An unexpected error occurred while fetching posts.', 'error');
      setProfilePosts([]);
    } finally {
      setLoading(false);
    }
  }, [engagementList, showAlert]);

  useEffect(() => {
    const checkSubscriptionAndAuth = async () => {
      setSubscriptionStatusLoading(true);
      console.log('Plan IDs from env:', { PRO_PLAN_ID, PRO_YEARLY_ID, BUSINESS_PLAN_ID, BUSINESS_YEARLY_ID });
      if (!checkAuth()) {
        setError('You must be logged in to use this feature');
        showAlert('Please log in to continue', 'error');
        setIsFeatureAllowed(false);
        setSubscriptionStatusLoading(false);
        return;
      }

      try {
        const response = await api.get('/api/subscriptions/status'); 
        const subscription = response.data.subscription;
        console.log('Received subscription:', subscription);

        if (subscription && subscription.status === 'active') {
          setCurrentPlanId(subscription.price_id);
          console.log('Comparing subscription price_id:', subscription.price_id, 'Type:', typeof subscription.price_id);
          console.log('Against PRO_PLAN_ID:', PRO_PLAN_ID, 'Type:', typeof PRO_PLAN_ID);
          console.log('Against BUSINESS_PLAN_ID:', BUSINESS_PLAN_ID, 'Type:', typeof BUSINESS_PLAN_ID);

          const isPro = subscription.price_id === PRO_PLAN_ID || subscription.price_id === PRO_YEARLY_ID;
          const isBusiness = subscription.price_id === BUSINESS_PLAN_ID || subscription.price_id === BUSINESS_YEARLY_ID;
          console.log('Comparison results:', { isPro, isBusiness });

          if (isPro || isBusiness) {
            setIsFeatureAllowed(true);
          } else {
            setIsFeatureAllowed(false);
          }
        } else {
          setIsFeatureAllowed(false);
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        showAlert('Could not verify subscription. Please try again later.', 'error');
        setIsFeatureAllowed(false);
      } finally {
        console.log('Final isFeatureAllowed state:', isFeatureAllowed);
        setSubscriptionStatusLoading(false);
      }
    };
    checkSubscriptionAndAuth();
  }, [PRO_PLAN_ID, PRO_YEARLY_ID, BUSINESS_PLAN_ID, BUSINESS_YEARLY_ID, showAlert]);

  const autoRefreshPosts = useCallback(() => {
    if (engagementList) {
      fetchProfilePosts();
    }
  }, [engagementList, fetchProfilePosts]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    
    if (newValue === 'posts' && engagementList && (!profilePosts || profilePosts.length === 0)) {
      fetchProfilePosts();
    }
  };
  
  const checkAuth = () => {
    const token = authService.getToken();
    const isValid = authService.isTokenValid();
    console.log('Auth token exists:', !!token);
    console.log('Auth token is valid:', isValid);
    return isValid;
  };
  
  const fetchEngagementListData = useCallback(async (retries = 3, attemptCreation = true) => {
      try {
        setLoading(true);
      console.log('Fetching engagement list, attempt:', 4 - retries);
        const data = await engagementService.getLists();
      
      let listToUse = null;
      if (Array.isArray(data) && data.length > 0) {
        listToUse = data[0];
        if (data.length > 1) {
          console.warn("Multiple lists returned, but component is set to handle a single list. Using the first one.");
        }
      } else if (data && typeof data === 'object' && !Array.isArray(data)) {
        listToUse = data;
      }

      if (listToUse && listToUse.id) {
        const detailedList = await engagementService.getList(listToUse.id);
        setEngagementList(detailedList);
        setShowListDetails(true);
        if (currentTab === 'posts' && detailedList.profiles?.length > 0) {
          setTimeout(() => fetchProfilePosts(detailedList), 100);
          }
        setError(null);
        setApiError('');
      } else if (attemptCreation) {
        console.warn("No engagement list found for the user. Attempting to create a default one.");
        try {
          const newListPayload = { name: "My Engagement Hub", description: "Default engagement list" };
          const createdList = await engagementService.createList(newListPayload); 
          if (createdList && createdList.id) {
            console.log("Successfully created a default engagement list:", createdList);
            await fetchEngagementListData(3, false);
            return;
        } else {
            console.error("Failed to create a default engagement list or list is invalid.");
            setError('Could not initialize your Engagement Hub. Please try refreshing the page.');
            setEngagementList({});
            setShowListDetails(true);
          }
        } catch (creationError) {
          console.error("Error creating a default engagement list:", creationError);
          setError('Error initializing your Engagement Hub. Please check console or try again later.');
          setEngagementList({});
          setShowListDetails(true);
        }
      } else {
        console.warn("Still no engagement list found, and creation was not attempted or failed.");
        setEngagementList({});
        setShowListDetails(true);
        setError('No engagement list available. Please try refreshing or contact support if this persists.'); 
      }
      } catch (err) {
      console.error('Error fetching engagement list (outer try-catch):', err);
        const errorMessage = 
          typeof err === 'string' ? err :
          err?.error || err?.message || 'Unknown error';
        
        setApiError(errorMessage);
        
      if (retries > 0 && attemptCreation) {
        setTimeout(() => fetchEngagementListData(retries - 1, true), 2000);
        } else {
        setError('Failed to load your engagement list. Please try refreshing.');
        setShowListDetails(false);
        setEngagementList(null);
        }
      } finally {
        setLoading(false);
      }
  }, [currentTab, showAlert]);

  useEffect(() => {
    if (subscriptionStatusLoading || !isFeatureAllowed) {
      if (!subscriptionStatusLoading && !isFeatureAllowed && !error) {
        // setError('This feature requires a Pro or Business plan.');
      } else if (!subscriptionStatusLoading && !isFeatureAllowed && error && error === 'You must be logged in to use this feature') {
        // Do nothing if already showing login error
      }
      return;
    }
    fetchEngagementListData();
  }, [subscriptionStatusLoading, isFeatureAllowed, fetchEngagementListData]);
  
  const handleRetry = () => {
    if (!isFeatureAllowed) return;
    setError(null);
    setApiError('');
    fetchEngagementListData();
  };

  const testApiDirectly = async () => {
    try {
      const testUsernames = ['hamzathebhatti', 'usamaejaz', 'creativesaadi']; // Testing multiple profiles
      console.log('Testing API directly for usernames:', testUsernames);
      
      const apiResponse = await api.post('/api/linkedin/engagement-profile-posts', {
        engageAccounts: testUsernames,
        forceApi: true
      });
      
      console.log('Direct API Response:', apiResponse.data);
      
      if (apiResponse.data.success && apiResponse.data.posts?.tweets?.length > 0) {
        // Group posts by profile
        const postsByProfile = apiResponse.data.posts.tweets.reduce((acc, post) => {
          const username = post.user?.username || extractUsername(post.user?.profile_url);
          if (username) {
            if (!acc[username]) acc[username] = [];
            acc[username].push(post);
          }
          return acc;
        }, {});

        console.log('Posts by profile:', postsByProfile);
        
        // Log summary
        Object.entries(postsByProfile).forEach(([username, posts]) => {
          console.log(`${username}: ${posts.length} posts`);
        });

        const totalPosts = apiResponse.data.posts.tweets.length;
        const profilesWithPosts = Object.keys(postsByProfile).length;
        
        showAlert(`Successfully fetched ${totalPosts} posts from ${profilesWithPosts} profiles`, 'success');
      } else {
        console.log('No posts found in API response');
        showAlert('No posts found in API response', 'warning');
      }
    } catch (error) {
      console.error('API Test Error:', error);
      showAlert(error.message || 'Error testing API', 'error');
    }
  };
  
  const handleDeleteList = async (listId, e) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to clear all profiles from your engagement list?')) {
      setLoading(true);
      try {
        setEngagementList(prev => ({ ...prev, profiles: [], profileCount: 0 }));
        setProfilePosts([]);
        showAlert('Engagement list cleared', 'success'); 
      } catch (err) {
        console.error('Error clearing list:', err);
        showAlert('Failed to clear list', 'error');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const [profileInputType, setProfileInputType] = useState('url'); // 'url' or 'username'
  const [newUsername, setNewUsername] = useState('');

  const handleAddProfile = async () => {
    if (!isFeatureAllowed || !engagementList || !engagementList.id) { 
       showAlert('Cannot add profile: Custom feed not available or invalid.', 'warning');
      return;
    }
    
    let profileToAdd = '';
    
    if (profileInputType === 'url') {
      if (!newProfileUrl.trim()) {
        showAlert('Please enter a valid LinkedIn profile URL', 'warning');
        return;
      }
      profileToAdd = newProfileUrl;
    } else {
      if (!newUsername.trim()) {
        showAlert('Please enter a valid LinkedIn username', 'warning');
        return;
      }
      // Convert username to LinkedIn URL format
      profileToAdd = `https://www.linkedin.com/in/${newUsername.trim()}/`;
    }
    
    setAddingProfile(true);
    try {
      const profileData = {
        profileUrl: profileToAdd,
        name: extractLinkedInName(profileToAdd)
      };
      
      // Add the profile via the service
      await engagementService.addProfile(engagementList.id, profileData);
      
      // Re-fetch the entire list from the server to ensure consistency and get the new profile ID
      const updatedList = await engagementService.getList(engagementList.id);
      setEngagementList(updatedList); // Update state with the complete list from server
      
      setNewProfileUrl('');
      setNewUsername('');
      setOpenAddProfileDialog(false);
      showAlert('Profile added successfully', 'success');

      // If on posts tab, trigger a refresh with the updated list
      if (currentTab === 'posts') {
        console.log('New profile added and on posts tab, fetching posts with updated list...');
        fetchProfilePosts(updatedList); // Pass the fresh list, forceApi defaults to false
      }

    } catch (err) {
      console.error('Error adding profile:', err);
      showAlert('Failed to add profile', 'error');
    } finally {
      setAddingProfile(false);
    }
  };
  
  const handleRemoveProfile = async (profileId) => {
    if (!engagementList || !engagementList.id) return;
    
    if (window.confirm('Are you sure you want to remove this profile?')) {
      setLoading(true);
      try {
        await engagementService.removeProfile(engagementList.id, profileId);
        
        setEngagementList(prevList => ({
          ...prevList,
          profiles: prevList.profiles.filter(profile => profile.id !== profileId),
          profileCount: prevList.profileCount - 1
        }));
        
        showAlert('Profile removed successfully', 'success');
      } catch (err) {
        console.error('Error removing profile:', err);
        showAlert('Failed to remove profile', 'error');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleEngageWithPost = (post, action) => {
    if (action === 'comment') {
      handleOpenCommentDialog(post);
    } else if (action === 'remix') {
      handleRemixClick(post);
    }
  };
  
  const renderListDetailsPage = () => {
    if (!engagementList) {
        if (!loading) {
            return (
                <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center', mt:3 }}>
                    <Typography variant="h5">Welcome to your Custom LinkedIn Feed</Typography>
                    <Typography>
                        {error ? error : "No custom feed found or still loading. Add LinkedIn profiles to get started or try refreshing."}
                    </Typography>
                    {error && <Button onClick={handleRetry} sx={{mt:2}}>Retry</Button>}
                </Paper>
            );
        }
        return null;
    }
  
  return (
        <Box className="content-container">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={0} className="content-paper" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', mb: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box>
                    <Typography variant="h5" component="h1" className="component-heading" sx={{ mb: 0.75, fontWeight: 600 }}>
                      Custom LinkedIn Feed
                    </Typography>
                    <Typography variant="body2" color="var(--text-secondary)">
                      Create your personalized LinkedIn feed from handpicked profiles. Stay updated with content from only the people you choose to follow.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper" 
                sx={{ 
                  p: 0, 
                  borderRadius: 2, 
                  backgroundColor: 'transparent',
                  boxShadow: 'none'
                }}
              >
                <Tabs 
                  value={currentTab} 
                  onChange={handleTabChange}
                  sx={{ 
                    borderBottom: 1, 
                    borderColor: 'var(--border-color)',
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      minHeight: '48px'
                    },
                    '& .Mui-selected': {
                      color: 'var(--primary-color)',
                      fontWeight: 600
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'var(--primary-color)'
                    }
                  }}
                >
                  <Tab 
                    label="Posts" 
                    value="posts" 
                  />
                  <Tab 
                    label="Profiles" 
                    value="profiles" 
                  />
                  {/* Commented out settings tab
                  <Tab 
                    label="Settings" 
                    value="settings" 
                  />
                  */}
                </Tabs>
                
                <TabPanel value={currentTab} index="posts">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ color: 'var(--text-primary)' }}>
                      Your Custom Feed
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        startIcon={<RefreshIcon />}
                        onClick={() => fetchProfilePosts(engagementList, true)}
                        disabled={loading || !engagementList?.profiles?.length}
                        sx={{ 
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          boxShadow: 'none',
                          bgcolor: 'var(--primary-color)',
                          '&:hover': {
                            bgcolor: 'var(--primary-dark)',
                          },
                        }}
                      >
                        Refresh Posts
                      </Button>
                      <Button 
                        variant="outlined"
                        onClick={testApiDirectly}
                        disabled={loading}
                        sx={{ 
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: 'var(--primary-color)',
                          color: 'var(--primary-color)',
                          '&:hover': {
                            borderColor: 'var(--primary-dark)',
                            backgroundColor: 'var(--primary-light)'
                          },
                        }}
                      >
                        Test API
                      </Button>
                    </Box>
                  </Box>
                  
                  {Object.keys(postSources).length > 0 && (
                    <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Object.entries(postSources).map(([username, source]) => (
                        <Chip
                          key={username}
                          label={`${username}: ${source === 'external_api' ? 'API' : 'LinkedIn'}`}
                          size="small"
                          color={source === 'external_api' ? 'primary' : 'secondary'}
                          sx={{ borderRadius: '4px' }}
                        />
                      ))}
                    </Box>
                  )}
                  
                  {loading && profilePosts?.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                      <CircularProgress sx={{ color: 'var(--primary-color)' }} />
                    </Box>
                  ) : profilePosts?.length === 0 ? (
                    <Box 
                      sx={{ 
                        p: 12, 
                        borderRadius: 2, 
                        border: '1px dashed var(--border-color)',
                        textAlign: 'center',
                        bgcolor: 'var(--background-light)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <Box sx={{fontSize: 80, color: 'var(--text-light)'}}>📄</Box>
                      <Box>
                        <Typography variant="h5" color="var(--text-secondary)" sx={{ mb: 2 }}>
                          Your feed is empty
                        </Typography>
                        <Typography variant="body1" color="var(--text-light)" sx={{ mb: 3 }}>
                          {engagementList?.profiles?.length > 0 
                            ? "Click 'Refresh Posts' to load the latest content from your feed sources" 
                            : "Add LinkedIn profiles in the Feed Sources tab to start building your custom feed"}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Masonry
                      breakpointCols={{
                        default: 3,
                        1200: 3,
                        900: 2,
                        600: 1
                      }}
                      className="masonry-grid"
                      columnClassName="masonry-grid_column"
                      style={{
                        display: 'flex',
                        marginLeft: '-16px',
                        width: 'auto'
                      }}
                    >
                      {profilePosts.map((post) => (
                        <div 
                          key={post.id || `post-${Math.random()}`}
                          style={{ marginBottom: '16px', paddingLeft: '16px' }}
                        >
                          <EngagementPostCard
                            post={post}
                            onCommentClick={() => handleOpenCommentDialog(post)}
                            onRemixClick={() => handleRemixClick(post)}
                          />
                        </div>
                      ))}
                    </Masonry>
                  )}
                </TabPanel>
                
                <TabPanel value={currentTab} index="profiles">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ color: 'var(--text-primary)' }}>
                      Feed Sources
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />}
                      onClick={() => setOpenAddProfileDialog(true)}
                      disabled={loading || !engagementList}
                      sx={{ 
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        bgcolor: 'var(--primary-color)',
                        '&:hover': {
                          bgcolor: 'var(--primary-dark)',
                        },
                      }}
                    >
                      Add Source
                    </Button>
                  </Box>
                  
                  {loading && !engagementList?.profiles?.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                      <CircularProgress sx={{ color: 'var(--primary-color)' }} />
                    </Box>
                  ) : !engagementList?.profiles || engagementList.profiles.length === 0 ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center', 
                        py: 12, 
                        px: 6,
                        borderRadius: 2, 
                        border: '1px dashed var(--border-color)',
                        bgcolor: 'var(--background-light)',
                        gap: 4
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 80, color: 'var(--text-light)' }} />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" color="var(--text-secondary)" sx={{ mb: 2 }}>
                          No feed sources added yet
                        </Typography>
                        <Typography variant="body1" color="var(--text-light)" sx={{ mb: 3 }}>
                          Add LinkedIn profiles to start building your personalized custom feed
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ borderRadius: 2, border: '1px solid var(--border-color)' }}>
                      <List sx={{ p: 0 }}>
                        {engagementList?.profiles?.map((profile, index) => (
                          <ListItem 
                            divider={index < engagementList.profiles.length - 1}
                            key={profile.id}
                            secondaryAction={
                              <Tooltip title="Remove from list">
                                <IconButton edge="end" color="error" onClick={() => handleRemoveProfile(profile.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            }
                            sx={{ py: 2 }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'var(--primary-color)' }}>
                                <PersonIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={profile.name || 'LinkedIn User'} 
                              secondary={profile.profileUrl}
                              primaryTypographyProps={{ fontWeight: 500, color: 'var(--text-primary)' }}
                              secondaryTypographyProps={{ color: 'var(--text-secondary)' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </TabPanel>
                
                {/* Commented out Settings tab panel
                <TabPanel value={currentTab} index="settings">
                  <Typography variant="h6" sx={{ mb: 3, color: 'var(--text-primary)' }}>
                    Engagement Hub Settings
                  </Typography>
                  
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid var(--border-color)', mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} color="var(--text-primary)">
                          Auto fetch lists
                        </Typography>
                        <Typography variant="body2" color="var(--text-secondary)">
                          Latest posts will auto refresh every day at the time you set.
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: 42, 
                        height: 22, 
                        bgcolor: autoFetchEnabled ? 'var(--success-color)' : '#f5f5f5', 
                        borderRadius: 11, 
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s',
                        border: '1px solid',
                        borderColor: autoFetchEnabled ? 'var(--success-color)' : 'var(--border-color)'
                      }}
                      onClick={() => setAutoFetchEnabled(!autoFetchEnabled)}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          width: 18, 
                          height: 18, 
                          bgcolor: '#fff', 
                          borderRadius: '50%', 
                          top: '1px', 
                          left: autoFetchEnabled ? '21px' : '1px',
                          transition: 'left 0.3s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }} />
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
                      Refresh post everyday at
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        border: '1px solid var(--border-color)', 
                        borderRadius: 1, 
                        p: 1, 
                        width: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Typography variant="body1" color="var(--text-primary)">
                          {refreshTime}
                        </Typography>
                        <Typography color="var(--text-secondary)" variant="body2">
                          UTC
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        border: '1px solid var(--border-color)', 
                        borderRadius: 1, 
                        p: 1, 
                        width: 80,
                        textAlign: 'center'
                      }}>
                        <Typography variant="body1" color="var(--text-primary)">
                          {refreshPeriod}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="var(--text-secondary)">
                        opt-out for email reminders
                      </Typography>
                      <Box sx={{ 
                        width: 42, 
                        height: 22, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 11, 
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s',
                        border: '1px solid var(--border-color)'
                      }}
                      onClick={() => setOptOutEmail(!optOutEmail)}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          width: 18, 
                          height: 18, 
                          bgcolor: '#fff', 
                          borderRadius: '50%', 
                          top: '1px', 
                          left: optOutEmail ? '21px' : '1px',
                          transition: 'left 0.3s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }} />
                      </Box>
                    </Box>
                  </Paper>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<SettingsIcon />}
                      onClick={handleUpdateList} 
                      disabled={loading || !engagementList}
                      sx={{ 
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'var(--primary-color)',
                        color: 'var(--primary-color)',
                        '&:hover': {
                          borderColor: 'var(--primary-dark)',
                          backgroundColor: 'var(--primary-light)'
                        },
                      }}
                    >
                      Save Settings
                    </Button>
                    {engagementList?.profiles?.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                          onClick={(e) => handleDeleteList(engagementList.id, e)}
                          disabled={loading || !engagementList}
                      sx={{ 
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                          Clear All Profiles
                    </Button>
                    )}
                  </Box>
                </TabPanel>
                */}
              </Paper>
            </Grid>
          </Grid>
        </Box>
    );
  };

  const renderCommentDialog = () => {
    if (!commentingPost) return null;
    
    const postAuthor = commentingPost.author || { name: 'LinkedIn User' };
    const isCommentTooLong = commentText.length > 700;
    
    return (
      <Dialog
        open={openCommentDialog}
        onClose={handleCloseCommentDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Comment on {postAuthor.name}'s post
          </Typography>
          <IconButton onClick={handleCloseCommentDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Original post - left side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            overflowY: 'auto',
            borderRight: { xs: 'none', md: '1px solid var(--border-color)' },
            borderBottom: { xs: '1px solid var(--border-color)', md: 'none' },
            height: { xs: '40%', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src={commentingPost.author?.profilePicture || commentingPost.author?.image || ''}
                alt={postAuthor.name}
                sx={{ width: 40, height: 40, mr: 1.5 }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                  {postAuthor.name}
                </Typography>
                <Typography variant="caption" color="var(--text-light)" sx={{ lineHeight: 1.3, display: 'block', mt: 0.3 }}>
                  {postAuthor.occupation || postAuthor.summary || 'LinkedIn User'}
                </Typography>
              </Box>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
                color: 'var(--text-primary)',
                fontSize: '0.95rem'
              }}
            >
              {commentingPost.text || commentingPost.zFull?.text || ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThumbUp fontSize="small" sx={{ color: '#0a66c2', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {commentingPost.numLikes || 0} Likes
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatBubbleOutline fontSize="small" sx={{ color: '#666', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {commentingPost.numComments || 0} Comments
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Comment section - right side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: { xs: '60%', md: '100%' },
            overflow: 'hidden'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography className="section-title" sx={{ mb: 0 }}>
                Write your comment
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={selectedCommentPrompt}
                    onChange={(e) => setSelectedCommentPrompt(e.target.value)}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.85rem',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--border-color)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0a66c2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0a66c2',
                      }
                    }}
                  >
                    {Object.keys(commentPrompts).map((promptType) => (
                      <MenuItem 
                        key={promptType} 
                        value={promptType}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {promptType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleGenerateComment}
                  disabled={generatingComment}
                  startIcon={generatingComment ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon fontSize="small" />}
                  sx={{ 
                    height: 38,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#0a66c2',
                    '&:hover': {
                      bgcolor: '#004182',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(10, 102, 194, 0.4)',
                      color: 'white'
                    }
                  }}
                >
                  {generatingComment ? 'Generating...' : 'AI Comment'}
                </Button>
              </Box>
            </Box>
          <TextField
            autoFocus
            multiline
              rows={8}
            variant="outlined"
            fullWidth
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            error={isCommentTooLong}
            helperText={isCommentTooLong ? "Comment exceeds LinkedIn's maximum character limit" : ""}
            sx={{
                flex: 1,
                mb: 2,
              '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                fontSize: '0.95rem'
              }
            }}
          />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', pt: 2, borderTop: '1px solid var(--border-color)' }}>
            <Typography 
              variant="caption" 
              color={isCommentTooLong ? "error" : "textSecondary"} 
                sx={{ fontWeight: isCommentTooLong ? 600 : 400 }}
            >
              {commentText.length}/700 characters
            </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button 
            onClick={handleCloseCommentDialog}
                  variant="outlined"
            sx={{ 
                    height: 44,
                    borderRadius: 2,
              textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'var(--border-color)',
              color: 'var(--text-secondary)',
                    '&:hover': {
                      borderColor: '#004182',
                      bgcolor: 'rgba(0, 65, 130, 0.04)'
                    }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={commentLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handlePostComment}
            disabled={!commentText.trim() || commentLoading || isCommentTooLong}
            sx={{ 
                    height: 44,
                    borderRadius: 2,
              textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#0a66c2',
              '&:hover': {
                      bgcolor: '#004182',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(10, 102, 194, 0.4)',
                color: 'white'
              }
            }}
          >
            {commentLoading ? 'Posting...' : 'Post Comment'}
          </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  const handleOpenCommentDialog = (post) => {
    setCommentingPost(post);
    setCommentText('');
    setOpenCommentDialog(true);
  };

  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
    setCommentingPost(null);
    setCommentText('');
  };

  const handlePostComment = async () => {
    if (!commentingPost || !commentText.trim()) {
      enqueueSnackbar('Please enter a comment', { variant: 'warning' });
      return;
    }

    if (commentText.length > 700) {
      enqueueSnackbar('Comment is too long. LinkedIn allows maximum 700 characters', { variant: 'warning' });
      return;
    }

    setCommentLoading(true);
    try {
      const postUrl = commentingPost.url || 
        (commentingPost.id && `https://www.linkedin.com/feed/update/urn:li:activity:${commentingPost.id}`);

      if (!postUrl) {
        throw new Error('Could not determine post URL');
      }

      window.open(postUrl, '_blank', 'noopener,noreferrer');
      handleCloseCommentDialog();
      enqueueSnackbar('Opening post in LinkedIn to comment', { variant: 'success' });
    } catch (error) {
      console.error('Error handling comment:', error);
      enqueueSnackbar('Failed to open post. Please try again.', { variant: 'error' });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleRemixClick = (post) => {
    setSelectedPost(post);
    setRewrittenContent('');
    setRewriteTopic('');
    setOpenRewritePanel(true);
  };

  // Add function to generate comment with AI
  const handleGenerateComment = async () => {
    if (!commentingPost) return;
    
    setGeneratingComment(true);
    try {
      const postText = commentingPost.text || commentingPost.zFull?.text || '';
      const authorName = commentingPost.user?.name || commentingPost.zFull?.poster?.first + ' ' + commentingPost.zFull?.poster?.last || 'LinkedIn User';
      
      // Call Azure OpenAI API (similar to the handleRewriteWithAI function)
      const response = await fetch('https://eai.openai.azure.com/openai/deployments/test/chat/completions?api-version=2024-02-15-preview', {
        method: 'POST',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36 Edg/135.0.0.0',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'api-key': process.env.REACT_APP_OPENAI_API_KEY,
          'content-type': 'application/json;charset=UTF-8',
          'origin': `chrome-extension://${process.env.REACT_APP_CHROME_EXTENSION_ID}`
        },
        body: JSON.stringify({
          "model": "gpt-4",
          "messages": [
            {
              "role": "user",
              "content": `${commentPrompts[selectedCommentPrompt]}\n\nLinkedIn Post:\n${postText}`
            }
          ],
          "temperature": 0.7,
          "max_tokens": 200
        })
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('AI Comment Response:', data);
      
      // Extract the generated comment
      const generatedComment = data.choices[0].message.content.trim();
      
      // Clean the comment text - remove quotes at beginning and end if present
      let cleanedComment = generatedComment;
      if (cleanedComment.startsWith('"') && cleanedComment.endsWith('"')) {
        cleanedComment = cleanedComment.substring(1, cleanedComment.length - 1);
      }
      
      // Set the comment text
      setCommentText(cleanedComment);
      
      enqueueSnackbar('Comment generated successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error generating comment:', error);
      enqueueSnackbar('Error generating comment. Please try again.', { variant: 'error' });
    } finally {
      setGeneratingComment(false);
    }
  };

  // Utility function to decode HTML entities in post content
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const result = textarea.value;
    textarea.remove();
    return result;
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num;
  };

  const handleRewriteWithAI = async () => {
    if (!selectedPost) {
      enqueueSnackbar('No post selected', { variant: 'warning' });
      return;
    }
    
    setRewriteLoading(true);
    try {
      const postBody = selectedPost.text || selectedPost.zFull?.text || '';
      
      const topicText = rewriteTopic.trim() 
        ? `to make it about the topic: ${rewriteTopic}` 
        : `to improve its engagement while keeping the same topic`;

      const prompt = `Rewrite the following LinkedIn post ${topicText}.

IMPORTANT: Maintain the exact same structure, formatting, and style as the original post, including:
- Keep the same paragraph breaks
- Preserve any bullet points or lists in the same format
- Maintain the same number of paragraphs
- Keep similar sentence length and complexity
- Preserve any call-to-action elements
- Maintain the same tone (professional, casual, etc.)
- If the original post ends with a specific instruction or pointer, keep that pattern

Original post:
${postBody}

Your rewrite should feel like the original author wrote it${rewriteTopic.trim() ? ` but about ${rewriteTopic} instead` : ' with improved wording and engagement'}.`;
      
      const response = await fetch('https://eai.openai.azure.com/openai/deployments/test/chat/completions?api-version=2024-02-15-preview', {
        method: 'POST',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36 Edg/135.0.0.0',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'api-key': process.env.REACT_APP_OPENAI_API_KEY,
          'content-type': 'application/json;charset=UTF-8',
          'origin': `chrome-extension://${process.env.REACT_APP_CHROME_EXTENSION_ID}`
        },
        body: JSON.stringify({
          "model": "gpt-4",
          "messages": [
            {
              "role": "user",
              "content": prompt
            }
          ],
          "temperature": 0.7,
          "max_tokens": 600
        })
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Rewrite API Response:', data);
      
      const generatedText = data.choices[0].message.content;
      setRewrittenContent(generatedText);
      
      enqueueSnackbar('Post rewritten successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error rewriting post:', error);
      enqueueSnackbar('Error rewriting post. Please try again.', { variant: 'error' });
    } finally {
      setRewriteLoading(false);
    }
  };

  const handleCopyRewrite = () => {
    if (rewrittenContent) {
      navigator.clipboard.writeText(rewrittenContent);
      enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
    }
  };

  useEffect(() => {
    const interval = setInterval(autoRefreshPosts, 86400000);
    return () => clearInterval(interval);
  }, [autoRefreshPosts]);

  const renderRewritePanel = () => {
    if (!selectedPost) return null;

  return (
      <Dialog
        open={openRewritePanel}
        onClose={() => setOpenRewritePanel(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Rewrite Post
          </Typography>
          <IconButton onClick={() => setOpenRewritePanel(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Optional: Rewrite post about a specific topic"
            variant="outlined"
            value={rewriteTopic}
            onChange={(e) => setRewriteTopic(e.target.value)}
            size="small"
            sx={{ mb: 2, borderRadius: 1 }}
          />
          <TextField
            multiline
            rows={10}
            variant="outlined"
            fullWidth
            placeholder="Rewritten post will appear here..."
            value={rewrittenContent}
            onChange={(e) => setRewrittenContent(e.target.value)}
            InputProps={{
              readOnly: rewriteLoading,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.95rem',
                bgcolor: rewriteLoading ? '#f5f5f5' : 'inherit'
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCopyRewrite}
            startIcon={<ContentCopyIcon />}
            disabled={!rewrittenContent || rewriteLoading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              borderRadius: 1
            }}
          >
            Copy
          </Button>
          <Box>
            <Button 
              onClick={() => setOpenRewritePanel(false)}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                color: 'var(--text-secondary)',
                borderRadius: 1,
                mr: 1
            }}
          >
            Cancel
          </Button>
        <Button 
          variant="contained" 
              startIcon={rewriteLoading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
            onClick={handleRewriteWithAI}
              disabled={rewriteLoading}
          sx={{ 
            textTransform: 'none',
              fontWeight: 500,
                        bgcolor: 'var(--primary-color)',
              borderRadius: 1,
              boxShadow: 'none',
                        '&:hover': {
                          bgcolor: 'var(--primary-dark)',
                boxShadow: 'none'
                        },
              '&.Mui-disabled': {
                bgcolor: 'rgba(10, 102, 194, 0.4)',
                color: 'white'
              }
          }}
        >
              {rewriteLoading ? 'Rewriting...' : 'Rewrite with AI'}
        </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  if (subscriptionStatusLoading) {
    return <LoadingScreen message="Loading Custom Feed..." />;
  }

  if (!isFeatureAllowed) {
    console.log('Rendering FeatureLockOverlay because isFeatureAllowed is false.');
    return (
      <Box sx={{ 
        position: 'relative', 
        height: 'calc(100vh - 64px)',
        overflow: 'hidden'
      }}>
        <Box sx={{ opacity: 0.4, height: '100%' }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Skeleton animation="wave" variant="text" width="40%" height={40} />
                <Skeleton animation="wave" variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                {Array.from(new Array(12)).map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, borderRadius: 2, height: 150 }}>
                      <Skeleton animation="wave" variant="text" width="70%" height={30} />
                      <Skeleton animation="wave" variant="text" width="50%" />
                      <Skeleton animation="wave" variant="text" width="90%" sx={{ mt: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Skeleton animation="wave" variant="circular" width={24} height={24} sx={{ mr: 1 }}/>
                        <Skeleton animation="wave" variant="circular" width={24} height={24} />
      </Box>
                </Paper>
              </Grid>
                ))}
              </Grid>
            </Paper>
          </Container>
        </Box>
        
        <FeatureLockOverlay featureName="Engagement Lists" currentPlan={currentPlanId} />
        </Box>
    );
  }

  if (loading && !engagementList) {
    return (
      <LoadingScreen message="Loading Custom Feed..." />
    );
  }

  return (
    <>
      <Helmet>
        <title>Custom LinkedIn Feed | Instation</title>
        <meta name="description" content="Create your personalized LinkedIn feed with handpicked profiles. See posts only from the people you choose to follow." />
      </Helmet>
      
      <Container 
        maxWidth={false} 
        sx={{ 
          py: 3, 
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: '100vw',
          overflow: 'visible'
        }}
      >
        <GlobalStyles styles={masonryStyles} />
        
        {error && !apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
            <Button size="small" onClick={handleRetry} sx={{ ml: 2 }}>Retry</Button>
          </Alert>
        )}
        
        {showListDetails && engagementList ? (
          renderListDetailsPage()
        ) : (
          !loading && !error && (
            <Paper sx={{ p:3, borderRadius: 2, textAlign: 'center', mt:3}}>
                <Typography variant="h5">Welcome to your Custom LinkedIn Feed</Typography>
                <Typography>Loading your custom feed. If this persists, please try refreshing.</Typography>
          </Paper>
          )
        )}

        <Dialog open={openAddProfileDialog} onClose={() => setOpenAddProfileDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Source to Your Custom Feed</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Button
                  variant={profileInputType === 'url' ? 'contained' : 'outlined'}
                  onClick={() => setProfileInputType('url')}
                  sx={{ 
                    flex: 1, 
                    mr: 1,
                    textTransform: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Profile URL
                </Button>
                <Button
                  variant={profileInputType === 'username' ? 'contained' : 'outlined'}
                  onClick={() => setProfileInputType('username')}
                  sx={{ 
                    flex: 1,
                    textTransform: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Username
                </Button>
              </Box>
            
              {profileInputType === 'url' ? (
                <TextField
                  autoFocus
                  margin="dense"
                  label="LinkedIn Profile URL"
                  fullWidth
                  variant="outlined"
                  value={newProfileUrl}
                  onChange={(e) => setNewProfileUrl(e.target.value)}
                  placeholder="https://www.linkedin.com/in/username/"
                />
              ) : (
                <TextField
                  autoFocus
                  margin="dense"
                  label="LinkedIn Username"
                  fullWidth
                  variant="outlined"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="username"
                  InputProps={{
                    startAdornment: <Box component="span" sx={{ color: 'text.secondary' }}>linkedin.com/in/</Box>
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddProfileDialog(false)}>Cancel</Button>
            <Button onClick={handleAddProfile} variant="contained" 
              disabled={addingProfile || 
                (profileInputType === 'url' && !newProfileUrl.trim()) || 
                (profileInputType === 'username' && !newUsername.trim()) || 
                !engagementList?.id}
            >
              {addingProfile ? <CircularProgress size={24} /> : 'Add Profile'}
            </Button>
          </DialogActions>
        </Dialog>
        {renderCommentDialog()}
        {renderRewritePanel()}

        <Snackbar 
          open={openAlert} 
          autoHideDuration={6000} 
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
            {alertMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default EngagementProfiles; 